import * as yup from 'yup';
import type { TFunction } from 'i18next';
import { MINIMUM_PASSWORD_LENGTH } from './authentication';
import { validateEmail } from './util';
import isMobilePhone from 'validator/lib/isMobilePhone';
import type { MobilePhoneLocale } from 'validator';
import { FiLanguage } from '../contexts/LocaleContext';

/**
 * Map FiLanguage to a validator phone locale.
 * FiLanguage values like 'en-FI' aren't valid validator locales,
 * so we map to the country's native locale or fall back to 'any'.
 */
function toPhoneLocale(language: FiLanguage): 'any' | MobilePhoneLocale {
  switch (language) {
    case FiLanguage.EN_FI:
      return 'fi-FI' as MobilePhoneLocale;
    case FiLanguage.EN_MT:
      return 'any';
    case FiLanguage.RO_RO:
      return 'ro-RO' as MobilePhoneLocale;
    case FiLanguage.DE_AT:
      return 'de-AT' as MobilePhoneLocale;
    default:
      return language as MobilePhoneLocale;
  }
}

const ZIPCODE_REGEX = /^(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/;
const ADDRESS_LINE_MAX_LENGTH = 40;

const NAME_MIN_LENGTH = 2;
const NAME_REGEX = /^\p{L}+[\p{L} ,.'-]*$/u;

export function addressValidationSchema(t: TFunction, language: FiLanguage) {
  return yup
    .object()
    .when('$requiresFirstAndLastName', {
      is: true,
      then: (schema) =>
        schema.shape({
          firstName: yup
            .string()
            .required(t('please_enter_your_first_name'))
            .matches(new RegExp(NAME_REGEX), t('first_name_must_start_with_letter'))
            .min(NAME_MIN_LENGTH, t('please_enter_your_full_first_name')),
          lastName: yup
            .string()
            .required(t('please_enter_your_last_name'))
            .matches(new RegExp(NAME_REGEX), t('last_name_must_start_with_letter'))
            .min(NAME_MIN_LENGTH, t('please_enter_your_full_last_name')),
        }),
    })
    .when('$requiresAccountDetails', {
      is: true,
      then: (schema) =>
        schema.shape({
          email: yup
            .string()
            .required(t('please_enter_your_email_address'))
            // Use our own validateEmail method instead of yup built-in because we look for .con in the email
            .test('validateEmail', t('please_enter_a_valid_email_address'), (value) => validateEmail(value ?? '')),
          // Password is optional when passwordlessCheckoutEnabled is true
          password: yup.string().when('$passwordlessCheckoutEnabled', {
            is: true,
            then: (pwdSchema) => pwdSchema.optional(),
            otherwise: (pwdSchema) =>
              pwdSchema
                .required(t('please_enter_a_password'))
                .min(
                  MINIMUM_PASSWORD_LENGTH,
                  t('password_must_be_at_least_characters', { length: MINIMUM_PASSWORD_LENGTH }),
                ),
          }),
        }),
    })
    .when('$requiresShippingDetails', {
      is: true,
      then: (schema) =>
        schema.shape({
          line1: yup
            .string()
            .required(t('please_enter_your_address'))
            .max(
              ADDRESS_LINE_MAX_LENGTH,
              t('address_line_1_can_be_at_most_characters', { length: ADDRESS_LINE_MAX_LENGTH }),
            ),
          line2: yup
            .string()
            .optional()
            .max(
              ADDRESS_LINE_MAX_LENGTH,
              t('address_line_2_can_be_at_most_characters', { length: ADDRESS_LINE_MAX_LENGTH }),
            ),
          city: yup
            .string()
            .required(t('please_enter_your_city'))
            .max(ADDRESS_LINE_MAX_LENGTH, t('city_can_be_at_most_characters', { length: ADDRESS_LINE_MAX_LENGTH })),
          state: yup
            .string()
            .when('$requiresState', {
              is: true,
              then: (stateSchema) => stateSchema.required(t('please_select_a_state')),
              otherwise: (stateSchema) => stateSchema.optional(),
            })
            .test(
              'vi-mini-restriction',
              t('fi_mini_not_supported_in_virgin_islands'),
              (value, context) => !(value === 'VI' && context.options.context?.hasMiniInCart),
            ),
          zip: yup
            .string()
            .required(t('please_enter_your_zip_code'))
            .matches(new RegExp(ZIPCODE_REGEX), t('please_enter_a_valid_zip_code')),
          phone: yup
            .string()
            .required(t('please_enter_your_phone_number'))
            .test('isValidMobilePhoneForRegion', t('please_enter_a_valid_phone_number'), (value) =>
              value ? isMobilePhone(value, toPhoneLocale(language)) : true,
            ),
          shippingCode: yup.string().required(t('please_select_a_shipping_method')),
        }),
    })
    .when('$requiresGiftRecipient', {
      is: true,
      then: (schema) =>
        schema.shape({
          giftFirstName: yup
            .string()
            .required(t('please_enter_the_recipients_name'))
            .matches(new RegExp(NAME_REGEX), t('first_name_must_start_with_letter')),
          giftLastName: yup
            .string()
            .required(t('please_enter_the_recipients_last_name'))
            .matches(new RegExp(NAME_REGEX), t('last_name_must_start_with_letter')),
        }),
    });
}
